import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { CheckCircle, Description, OpenInNew, Refresh, SkipNext } from "@mui/icons-material";

export type OnboardingConsentMode = "none" | "optional" | "required";
export type OnboardingConsentStatus =
  | "pending"
  | "invited"
  | "completed"
  | "declined"
  | "withdrawn"
  | "expired"
  | "superseded";

export interface OnboardingConsentState {
  mode: OnboardingConsentMode;
  status: OnboardingConsentStatus;
  isRequired?: boolean;
  consentId?: string | null;
  consentFormId?: string | null;
  versionId?: string | null;
  signedPdfAvailable: boolean;
}

interface ConsentStepProps {
  participantId: string;
  studyCode: string;
  participantEmail?: string;
  participantPhone?: string;
  initialStatus?: OnboardingConsentState | null;
  onStatusChange?: (status: OnboardingConsentState) => void;
  onContinue: () => void;
  onSkip?: () => void;
  onBack?: () => void;
}

const API_BASE = String(process.env.REACT_APP_API_URL || "http://localhost:3002").replace(/\/$/, "");

async function readJson<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(data?.error || data?.message || `Request failed: ${response.status}`);
  }
  return data as T;
}

export async function fetchOnboardingConsentStatus(
  participantId: string,
  studyCode: string,
): Promise<OnboardingConsentState> {
  const params = new URLSearchParams({ participantId, studyCode });
  const response = await fetch(`${API_BASE}/api/consent/onboarding-status?${params.toString()}`, {
    credentials: "include",
  });
  return readJson<OnboardingConsentState>(response);
}

export default function ConsentStep({
  participantId,
  studyCode,
  participantEmail,
  participantPhone,
  initialStatus,
  onStatusChange,
  onContinue,
  onSkip,
  onBack,
}: ConsentStepProps) {
  const [consentState, setConsentState] = useState<OnboardingConsentState | null>(initialStatus || null);
  const [busy, setBusy] = useState<"idle" | "loading" | "opening" | "pdf">("idle");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [lastLink, setLastLink] = useState<string | null>(null);

  useEffect(() => {
    setConsentState(initialStatus || null);
  }, [initialStatus]);

  const status = consentState?.status || "pending";
  const isRequired = consentState?.mode === "required" || consentState?.isRequired;
  const isCompleted = status === "completed";

  const updateStatus = useCallback(
    (next: OnboardingConsentState) => {
      setConsentState(next);
      onStatusChange?.(next);
    },
    [onStatusChange],
  );

  const refreshStatus = useCallback(async () => {
    setBusy("loading");
    setError("");
    try {
      const next = await fetchOnboardingConsentStatus(participantId, studyCode);
      updateStatus(next);
      if (next.status === "completed") {
        setNotice("Consent has been recorded. You can continue to Fitbit authorization.");
      } else if (next.status === "expired") {
        setNotice("The previous consent link expired. Open the consent form again to continue.");
      } else {
        setNotice("Consent has not been completed yet. Submit the form in the opened tab, then refresh here.");
      }
      return next;
    } catch (err: any) {
      setError(err?.message || "Failed to refresh consent status.");
      return null;
    } finally {
      setBusy("idle");
    }
  }, [participantId, studyCode, updateStatus]);

  useEffect(() => {
    if (!consentState) {
      refreshStatus();
      return;
    }
    if (status === "pending" || status === "invited") {
      const timer = window.setInterval(() => {
        refreshStatus();
      }, 5000);
      return () => window.clearInterval(timer);
    }
    return undefined;
  }, [consentState, refreshStatus, status]);

  const openConsent = useCallback(async () => {
    setBusy("opening");
    setError("");
    setNotice("");
    try {
      const response = await fetch(`${API_BASE}/api/consent/generate-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          participantId,
          studyCode,
          participantEmail,
          participantPhone,
        }),
      });
      const result = await readJson<{ link: string; consentId?: string; alreadyCompleted?: boolean }>(response);
      if (result.alreadyCompleted) {
        const next = await refreshStatus();
        if (next?.status === "completed") return;
      }
      setLastLink(result.link);
      const opened = window.open(result.link, "_blank", "noopener,noreferrer");
      if (!opened) {
        setNotice("Pop-up blocked. Use the manual link below to open the consent form.");
      } else {
        setNotice("Consent form opened in a new tab. Return here after submitting it.");
      }
      await refreshStatus();
    } catch (err: any) {
      setError(err?.message || "Failed to open the consent form.");
    } finally {
      setBusy("idle");
    }
  }, [participantEmail, participantId, participantPhone, refreshStatus, studyCode]);

  const previewSignedConsent = useCallback(async () => {
    if (!consentState?.consentId) return;
    setBusy("pdf");
    setError("");
    try {
      const params = new URLSearchParams({ participantId, studyCode });
      const url = `${API_BASE}/api/consent/participant/${encodeURIComponent(consentState.consentId)}/pdf?${params.toString()}`;
      const opened = window.open(url, "_blank", "noopener,noreferrer");
      if (!opened) setNotice("Pop-up blocked. Use your browser settings to allow the signed consent preview.");
    } catch (err: any) {
      setError(err?.message || "Failed to open signed consent preview.");
    } finally {
      setBusy("idle");
    }
  }, [consentState?.consentId, participantId, studyCode]);

  const statusChip = useMemo(() => {
    if (isCompleted) return <Chip color="success" label="Consent completed" />;
    if (status === "declined") return <Chip color="warning" label="Consent declined" />;
    if (status === "expired") return <Chip color="warning" label="New consent needed" />;
    return <Chip color={isRequired ? "primary" : "default"} label={isRequired ? "Required" : "Optional"} />;
  }, [isCompleted, isRequired, status]);

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={2.5}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Description color="primary" />
          <Typography variant="h6">Consent</Typography>
          {statusChip}
        </Stack>

        <Typography variant="body2" color="text.secondary">
          {isRequired
            ? "This study requires you to complete the consent form before connecting your Fitbit account."
            : "This study has an optional consent form. You can complete it now or skip it and continue to Fitbit authorization."}
        </Typography>

        {error && <Alert severity="error">{error}</Alert>}
        {notice && <Alert severity={isCompleted ? "success" : "info"}>{notice}</Alert>}

        <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Current status
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isCompleted
              ? "Your consent has been recorded for the current default form."
              : status === "expired"
                ? "A previous consent does not match the current default form. Please open and complete the current form."
                : "After submitting the consent form, return here and refresh the status."}
          </Typography>
        </Box>

        {lastLink && (
          <Button variant="text" href={lastLink} target="_blank" rel="noreferrer" startIcon={<OpenInNew />}>
            Open consent link manually
          </Button>
        )}

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <Button
            variant="contained"
            startIcon={busy === "opening" ? <CircularProgress color="inherit" size={18} /> : <OpenInNew />}
            onClick={openConsent}
            disabled={busy !== "idle" || isCompleted}
          >
            {busy === "opening" ? "Opening..." : "Open Consent Form"}
          </Button>
          <Button
            variant="outlined"
            startIcon={busy === "loading" ? <CircularProgress size={18} /> : <Refresh />}
            onClick={refreshStatus}
            disabled={busy !== "idle"}
          >
            Refresh Status
          </Button>
          {isCompleted && consentState?.signedPdfAvailable && (
            <Button variant="outlined" onClick={previewSignedConsent} disabled={busy !== "idle"}>
              Preview Signed Consent
            </Button>
          )}
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="flex-end">
          {onBack && (
            <Button variant="outlined" onClick={onBack} disabled={busy !== "idle"}>
              Back
            </Button>
          )}
          {!isRequired && !isCompleted && onSkip && (
            <Button variant="text" onClick={onSkip} endIcon={<SkipNext />} disabled={busy !== "idle"}>
              Skip for now
            </Button>
          )}
          <Button variant="contained" color="success" startIcon={<CheckCircle />} onClick={onContinue} disabled={!isCompleted}>
            Continue
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}
